// Renders sky targets (laser-destroyable) and kill rings (golden torus
// dropped at every kill — players can boost toward them). Both use
// InstancedMeshes scaled / positioned per-frame from server snapshots.

import * as THREE from "three";
import type { TargetSnapshot, KillRingSnapshot } from "../shared/protocol";

const MAX_TARGETS = 64;
const MAX_RINGS   = 32;

export class SkyEntities {
  group = new THREE.Group();
  private targets: THREE.InstancedMesh;
  private targetGlow: THREE.InstancedMesh;
  private rings: THREE.InstancedMesh;
  private ringInner: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();

  // Currently visible counts so we can hide stale slots.
  private targetCount = 0;
  private ringCount = 0;

  constructor() {
    this.group.name = "SkyEntities";

    // Target = floating crystal (icosahedron). Two layers: solid + glow halo.
    const tGeo = new THREE.IcosahedronGeometry(1, 0);
    const tMat = new THREE.MeshLambertMaterial({ color: 0xffd166, flatShading: true, emissive: 0x553300 });
    this.targets = new THREE.InstancedMesh(tGeo, tMat, MAX_TARGETS);
    this.targets.frustumCulled = false;
    this.targets.count = 0;
    this.group.add(this.targets);

    const gGeo = new THREE.SphereGeometry(1.6, 12, 8);
    const gMat = new THREE.MeshBasicMaterial({
      color: 0xffaa44, transparent: true, opacity: 0.18,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.targetGlow = new THREE.InstancedMesh(gGeo, gMat, MAX_TARGETS);
    this.targetGlow.frustumCulled = false;
    this.targetGlow.count = 0;
    this.group.add(this.targetGlow);

    // Kill ring = big golden torus, with an inner thinner accent.
    const rGeo = new THREE.TorusGeometry(12, 1.4, 12, 32);
    const rMat = new THREE.MeshBasicMaterial({
      color: 0xffe080, transparent: true, opacity: 0.85,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.rings = new THREE.InstancedMesh(rGeo, rMat, MAX_RINGS);
    this.rings.frustumCulled = false;
    this.rings.count = 0;
    this.group.add(this.rings);

    const riGeo = new THREE.TorusGeometry(12, 0.4, 6, 32);
    const riMat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.ringInner = new THREE.InstancedMesh(riGeo, riMat, MAX_RINGS);
    this.ringInner.frustumCulled = false;
    this.ringInner.count = 0;
    this.group.add(this.ringInner);
  }

  setTargets(targets: TargetSnapshot[], time: number) {
    let visible = 0;
    for (const t of targets) {
      if (!t.alive) continue;
      if (visible >= MAX_TARGETS) break;
      // Idle bob + spin so they don't look static.
      const bob = Math.sin(time * 0.0009 + t.id * 1.7) * 1.5;
      this.dummy.position.set(t.x, t.y + bob, t.z);
      this.dummy.scale.setScalar(t.r);
      this.dummy.rotation.set(time * 0.0006, time * 0.0011 + t.id, 0);
      this.dummy.updateMatrix();
      this.targets.setMatrixAt(visible, this.dummy.matrix);
      // Glow: same position, scale is constant relative size
      this.dummy.scale.setScalar(t.r);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.targetGlow.setMatrixAt(visible, this.dummy.matrix);
      visible++;
    }
    this.targets.count = visible;
    this.targetGlow.count = visible;
    this.targetCount = visible;
    this.targets.instanceMatrix.needsUpdate = true;
    this.targetGlow.instanceMatrix.needsUpdate = true;
  }

  setRings(rings: KillRingSnapshot[], time: number) {
    let visible = 0;
    for (const r of rings) {
      if (visible >= MAX_RINGS) break;
      // Slow slow-rotation around vertical so rings catch the eye.
      this.dummy.position.set(r.x, r.y, r.z);
      // Lay the torus flat (default torus has axis along +Z; rotate so it
      // lies in the XZ plane = facing up, like a portal floating).
      this.dummy.rotation.set(Math.PI / 2, time * 0.0007 + r.id, 0);
      // Pulse: scale wobbles slightly with TTL fading at the end.
      const fadeIn = Math.min(1, (20 - r.ttl) / 0.4);
      const fadeOut = Math.min(1, r.ttl / 1.5);
      const pulse = 1 + Math.sin(time * 0.005 + r.id) * 0.05;
      this.dummy.scale.setScalar(pulse * fadeIn);
      this.dummy.updateMatrix();
      this.rings.setMatrixAt(visible, this.dummy.matrix);
      this.ringInner.setMatrixAt(visible, this.dummy.matrix);
      // Opacity via vertex colors isn't easy with InstancedMesh; rely on
      // material opacity + visual fade via scale (already does fade-in).
      visible++;
      // Use fadeOut to dim near end-of-life
      void fadeOut;
    }
    this.rings.count = visible;
    this.ringInner.count = visible;
    this.ringCount = visible;
    this.rings.instanceMatrix.needsUpdate = true;
    this.ringInner.instanceMatrix.needsUpdate = true;
  }
}
